export const UI_ART_IDS = [
  "ammo_L",
  "ammo_L_empty",
  "ammo_M",
  "ammo_M_empty",
  "ammo_S",
  "ammo_S_empty",
  "ammo_tray",
  "ammo_tray_blank",
  "badge_ribbon_blank",
  "badge_draw",
  "badge_lose",
  "badge_win",
  "btn_block_normal",
  "btn_block_pressed",
  "btn_danger_normal",
  "btn_danger_pressed",
  "btn_dodge_normal",
  "btn_dodge_pressed",
  "btn_mute_normal",
  "btn_mute_pressed",
  "btn_offer_normal",
  "btn_offer_pressed",
  "btn_pause_normal",
  "btn_pause_pressed",
  "btn_primary_normal",
  "btn_primary_pressed",
  "btn_secondary_normal",
  "btn_secondary_pressed",
  "btn_unmute_normal",
  "cooldown_ready_ring",
  "cooldown_sweep",
  "hint_arrow_swipe_lr",
  "hint_arrow_swipe_up",
  "hint_finger",
  "hint_label_pill",
  "hint_ring_circle",
  "hp_seg_blue_empty",
  "hp_seg_blue_full",
  "hp_seg_blue_ghost",
  "hp_seg_red_empty",
  "hp_seg_red_full",
  "hp_seg_red_ghost",
  "hud_badge_blue",
  "hud_badge_red",
  "icon_coin",
  "icon_gem",
  "logo",
  "panel_card",
  "sheet_bottom",
  "splash_bg",
  "tag_bot",
  "tag_rec",
  "timer_fill_urgent",
  "timer_fill_yellow",
  "timer_pill_last3",
  "timer_pill_normal",
] as const;

export type UiArtId = (typeof UI_ART_IDS)[number];

const images = new Map<string, HTMLImageElement>();

export function uiAssetForState(id: string, pressed: boolean): string {
  return `${id}_${pressed ? "pressed" : "normal"}`;
}

export async function preloadUiArt(): Promise<void> {
  if (typeof Image === "undefined") return;
  await Promise.all(
    UI_ART_IDS.map(
      (id) =>
        new Promise<void>((resolve, reject) => {
          const image = new Image();
          image.decoding = "async";
          image.onload = () => {
            images.set(id, image);
            resolve();
          };
          image.onerror = () =>
            reject(new Error(`Unable to load UI art: ${id}`));
          image.src = `/art/ui/png/${id}.png`;
        }),
    ),
  );
}

export function getUiArt(id: string): HTMLImageElement | undefined {
  return images.get(id);
}

export function drawArt(
  context: CanvasRenderingContext2D,
  id: string,
  x: number,
  y: number,
  width?: number,
  height?: number,
): boolean {
  const image = getUiArt(id);
  if (!image) return false;
  context.drawImage(
    image,
    x,
    y,
    width ?? image.width / 3,
    height ?? image.height / 3,
  );
  return true;
}

export function drawNineSlice(
  context: CanvasRenderingContext2D,
  id: string,
  x: number,
  y: number,
  width: number,
  height: number,
  insetsPt: { left: number; right: number; top?: number; bottom?: number },
): boolean {
  const image = getUiArt(id);
  if (!image) return false;
  const scale = 3;
  const left = insetsPt.left * scale;
  const right = insetsPt.right * scale;
  const top = (insetsPt.top ?? 0) * scale;
  const bottom = (insetsPt.bottom ?? 0) * scale;
  const sx = [0, left, image.width - right];
  const sy = [0, top, image.height - bottom];
  const sw = [left, image.width - left - right, right];
  const sh = [top, image.height - top - bottom, bottom];
  const dx = [x, x + insetsPt.left, x + width - insetsPt.right];
  const dy = [y, y + (insetsPt.top ?? 0), y + height - (insetsPt.bottom ?? 0)];
  const dw = [
    insetsPt.left,
    width - insetsPt.left - insetsPt.right,
    insetsPt.right,
  ];
  const dh = [
    insetsPt.top ?? 0,
    height - (insetsPt.top ?? 0) - (insetsPt.bottom ?? 0),
    insetsPt.bottom ?? 0,
  ];
  for (let row = 0; row < 3; row++) {
    for (let column = 0; column < 3; column++) {
      if (
        sw[column]! <= 0 ||
        sh[row]! <= 0 ||
        dw[column]! <= 0 ||
        dh[row]! <= 0
      )
        continue;
      context.drawImage(
        image,
        sx[column]!,
        sy[row]!,
        sw[column]!,
        sh[row]!,
        dx[column]!,
        dy[row]!,
        dw[column]!,
        dh[row]!,
      );
    }
  }
  return true;
}
